import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '@/components/icons';
import { useSession } from '@/hooks';
import { useAppContext } from '@/hooks';
import { ContextSwitcher } from '@/components/context/ContextSwitcher';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EmptyState } from '@/components/ui';
import {
  C1ActionCard,
  C1Chip,
  C1Grid,
  C1Link,
  C1PageHeader,
  C1Section,
} from '@/components/c1';
import { CommandPalette, type PaletteMode } from '@/components/command/CommandPalette';
import { useCommandHistory } from '@/hooks/useCommandHistory';
import { destinationsFor } from '@/components/command/destinations';
import { InsightsPanel } from '@/components/insights/InsightsPanel';
import { StudentIdentityCard } from '@/components/student/StudentIdentityCard';
import { StudentStudyDesk } from '@/components/student/StudentStudyDesk';
import { useEduCoreLaunchUrl } from '@/hooks/useEduCoreLaunchUrl';
import { EduCorePaths } from '@/lib/educoreLinks';

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, session } = useSession();
  const { activeContext, availableContexts } = useAppContext();
  const educore = useEduCoreLaunchUrl(activeContext?.institution?.id);
  const [contextOpen, setContextOpen] = useState(false);
  const [palette, setPalette] = useState<PaletteMode | null>(null);
  const [studyDeskOpen, setStudyDeskOpen] = useState(false);
  // Scoped once, and both the palette and the history are built from the same list — so a
  // destination that is not offered also cannot be remembered or pinned. Deriving them
  // separately is how the two drift and a stale favourite outlives its grant.
  const destinations = useMemo(
    () =>
      destinationsFor({
        roleCode: activeContext?.role?.code,
        realmRoles: session?.realmRoles,
      }),
    [activeContext?.role?.code, session?.realmRoles],
  );

  const allowedIds = useMemo(() => destinations.map((d) => d.id), [destinations]);

  const { recent, favourites, remember, toggleFavourite } = useCommandHistory({
    subject: session?.identityId,
    institutionId: activeContext?.institution?.id,
    roleCode: activeContext?.role?.code,
    allowedIds,
  });

  // Ctrl/Cmd+K from anywhere on this page. The four cards below are the discoverable way
  // in; this is the one an administrator doing the same three things every morning will
  // actually use.
  const openSearch = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      setPalette('search');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', openSearch);
    return () => window.removeEventListener('keydown', openSearch);
  }, [openSearch]);

  const contextRole = activeContext?.role;
  const contextInstitution = activeContext?.institution;
  const contextGroup = activeContext?.group;
  // A group context is as renderable as an institution one: the group
  // administrator's home is the group's own dashboard.
  const hasRenderableContext = Boolean(contextRole && (contextInstitution || contextGroup));

  const financeHome = [
    'BURSAR',
    'ACCOUNTANT',
    'CASHIER',
    'FINANCIAL_CONTROLLER',
  ].includes(contextRole?.code ?? '');
  const studentHome = contextRole?.code === 'STUDENT';
  // The seats whose insights resolve to the academic overview. A director's morning
  // is approvals, performance and money — never a generic "New" palette whose every
  // entry is somebody else's job.
  const leadershipHome = [
    'DIRECTOR',
    'PRINCIPAL',
    'DEAN',
    'HOD',
    'ACADEMIC_DIRECTOR',
  ].includes(contextRole?.code ?? '');
  // Only the roles the approvals surface actually serves get the card; the others
  // are offered scan-and-verify, which every staff seat may use.
  const approverRole = ['DEAN', 'PRINCIPAL', 'ACADEMIC_DIRECTOR'].includes(
    contextRole?.code ?? '',
  );

  const hasNoAccess = status === 'authenticated' && availableContexts.length === 0;

  if (hasNoAccess) {
    return (
      <EmptyState icon={<Icon name="noAccess" size={48} />} title={t('auth.noAccess')} description={t('auth.noAccessDesc')} />
    );
  }

  const firstName = session?.name?.split(' ')[0] || '';

  return (
    <div className="page-home">
      {/* The seat is stated in the header rather than buried in prose: it decides what
          every figure below means, and a reader who misreads it misreads the page. */}
      <C1PageHeader
        eyebrow={t('home.overview')}
        title={`${t('home.welcome')}${firstName ? `, ${firstName}` : ''}`}
        subtitle={t('context.operatingAs')}
        actions={
          <>
            {hasRenderableContext ? (
              <C1Chip tone="accent">
                {contextRole?.name} · {contextInstitution?.name ?? contextGroup?.name}
              </C1Chip>
            ) : (
              <C1Chip>{t('context.noContexts')}</C1Chip>
            )}
            {availableContexts.length > 1 && (
              <C1Link onClick={() => setContextOpen(true)} aria-label={t('context.switchContext')}>
                {t('context.switchContext')}
              </C1Link>
            )}
          </>
        }
      />

      <ContextSwitcher open={contextOpen} onClose={() => setContextOpen(false)} />
      {/* Mounted only while open. The component guards on `open` too, but a mounted
          dialog still runs its hooks — including useNavigate, which requires a Router —
          so a closed palette would impose a routing context on every page that hosts it
          and do nothing useful in exchange. */}
      {palette !== null && (
        <CommandPalette
          open
          mode={palette ?? 'search'}
          destinations={destinations}
          onClose={() => setPalette(null)}
          recent={recent}
          favourites={favourites}
          onVisit={remember}
          onToggleFavourite={toggleFavourite}
        />
      )}
      {studentHome && (
        <StudentStudyDesk
          open={studyDeskOpen}
          onClose={() => setStudyDeskOpen(false)}
          hrefFor={educore.href}
          onFallback={() => navigate('/products')}
        />
      )}

      {hasRenderableContext && (
        <div className="home-dashboard">
          <C1Section title={t('home.quickActions')}>
            <C1Grid min={240} role="group" aria-label={t('home.quickActions')}>
              {financeHome ? (
                <>
                  {/*
                    Operational finance lives in EduCore. When a launch URL is configured,
                    these open the matching SPA path; otherwise they fall back to Products
                    so Cécile is never left on a CampusOne 404.
                  */}
                  <QuickAction
                    name="report"
                    label={t('home.finance.openEducore')}
                    hint={t('home.finance.openEducoreHint')}
                    onClick={() => {
                      const href = educore.href(EduCorePaths.overview);
                      if (href) window.open(href, '_blank', 'noopener,noreferrer');
                      else navigate('/products');
                    }}
                  />
                  <QuickAction
                    name="warning"
                    label={t('home.finance.reviewOutstanding')}
                    hint={t('home.finance.reviewOutstandingHint')}
                    onClick={() => {
                      const href = educore.href(EduCorePaths.fees);
                      if (href) window.open(href, '_blank', 'noopener,noreferrer');
                      else navigate('/products');
                    }}
                  />
                  <QuickAction
                    name="checklist"
                    label={t('home.finance.billing')}
                    hint={t('home.finance.billingHint')}
                    onClick={() => {
                      const href = educore.href(EduCorePaths.feeConfiguration);
                      if (href) window.open(href, '_blank', 'noopener,noreferrer');
                      else navigate('/products');
                    }}
                  />
                  <QuickAction
                    name="search"
                    label={t('command.search')}
                    hint="Ctrl K"
                    onClick={() => setPalette('search')}
                  />
                </>
              ) : studentHome ? (
                <>
                  {/*
                    Students get a Study desk instead of the generic "New" palette —
                    registration, results, Form B and fees all live in EduCore.
                  */}
                  <QuickAction
                    name="search"
                    label={t('command.search')}
                    hint="Ctrl K"
                    onClick={() => setPalette('search')}
                  />
                  <QuickAction
                    name="academic"
                    label={t('home.studyDesk.title')}
                    hint={t('home.studyDesk.hint')}
                    onClick={() => setStudyDeskOpen(true)}
                  />
                  <QuickAction
                    name="clock"
                    label={t('command.recent')}
                    badge={recent.length || undefined}
                    onClick={() => setPalette('recent')}
                  />
                  <QuickAction
                    name="star"
                    label={t('command.favourites')}
                    badge={favourites.length || undefined}
                    onClick={() => setPalette('favourites')}
                  />
                </>
              ) : leadershipHome ? (
                <>
                  {/*
                    Leadership seats: the three things a head of institution does from
                    here every morning, plus global search. Deep links open EduCore's
                    matching screen when a launch URL exists and fall back to Products
                    rather than a CampusOne 404.
                  */}
                  <QuickAction
                    name="search"
                    label={t('command.search')}
                    hint="Ctrl K"
                    onClick={() => setPalette('search')}
                  />
                  {approverRole ? (
                    <QuickAction
                      name="check"
                      label={t('home.leadership.reviewApprovals')}
                      hint={t('home.leadership.reviewApprovalsHint')}
                      onClick={() => navigate('/approvals')}
                    />
                  ) : (
                    <QuickAction
                      name="checkmark"
                      label={t('home.leadership.verify')}
                      hint={t('home.leadership.verifyHint')}
                      onClick={() => navigate('/verify')}
                    />
                  )}
                  <QuickAction
                    name="academic"
                    label={t('home.leadership.academicPerformance')}
                    hint={t('home.leadership.academicPerformanceHint')}
                    onClick={() => {
                      const href = educore.href(EduCorePaths.directorOverview);
                      if (href) window.open(href, '_blank', 'noopener,noreferrer');
                      else navigate('/products');
                    }}
                  />
                  <QuickAction
                    name="report"
                    label={t('home.leadership.financialPosition')}
                    hint={t('home.leadership.financialPositionHint')}
                    onClick={() => {
                      const href = educore.href(EduCorePaths.reports);
                      if (href) window.open(href, '_blank', 'noopener,noreferrer');
                      else navigate('/products');
                    }}
                  />
                </>
              ) : (
                <>
                  <QuickAction
                    name="search"
                    label={t('command.search')}
                    hint="Ctrl K"
                    onClick={() => setPalette('search')}
                  />
                  <QuickAction name="plus" label={t('command.new')} onClick={() => setPalette('new')} />
                  <QuickAction
                    name="clock"
                    label={t('command.recent')}
                    badge={recent.length || undefined}
                    onClick={() => setPalette('recent')}
                  />
                  <QuickAction
                    name="star"
                    label={t('command.favourites')}
                    badge={favourites.length || undefined}
                    onClick={() => setPalette('favourites')}
                  />
                </>
              )}
            </C1Grid>
          </C1Section>

          {contextRole?.code === 'STUDENT' && contextInstitution?.id ? (
            <StudentIdentityCard institutionId={contextInstitution.id} />
          ) : null}

          <InsightsPanel />
        </div>
      )}
    </div>
  );
}

function QuickAction({
  name,
  label,
  hint,
  badge,
  onClick,
}: {
  name: IconName;
  label: string;
  hint?: string;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <C1ActionCard
      icon={<Icon name={name} size={20} />}
      label={label}
      hint={hint}
      badge={badge}
      onClick={onClick}
      aria-label={label}
    />
  );
}
